import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import React, { ReactNode } from "react";
import { forwardRef } from "react";
import { sizes } from "../../../constants/spacing";
import { useTheme } from "../../../theme/utils/useTheme";
import { useMenuParentContext } from "../context/MenuParentContext";
import { menuTheme } from "../styled/menuTheme";
import { MenuLayoutOptions } from "../types/Menu";
import { MenuItemBase, MenuItemBaseProps } from "./MenuItemBase";

interface MenuItemDefaultStyleProps {
    isMultiLine: boolean;
}

// It's necessary to explicitly specify top/bottom padding when multiline, because the
// before/after content needs to be aligned with the first line of text, whereas the text needs to be center aligned.
// Without the padding, the before/after content would be too high. 6px is just the right amount of padding to get a
// target height of xxLarge (36px) with exactly 2 lines of text. Anything with more than 2 lines will scale and still
// have enough padding to not look squished.
const MenuItemDefaultStyle = styled(MenuItemBase)<MenuItemDefaultStyleProps>`
    justify-content: space-between;
    ${({ isMultiLine }) =>
        isMultiLine
            ? `
                align-items: flex-start;
                min-height: ${sizes.xxLarge}px;
                padding-top: 6px;
                padding-bottom: 6px;
                `
            : ""}
`;

interface StyledContentBeforeProps {
    isMultiLine: boolean;
    layout: MenuLayoutOptions;
}

const StyledContentBefore = styled("div")<StyledContentBeforeProps>(({ isMultiLine, layout }) => ({
    marginRight: `var(--sds--menu-item--gap--${layout}, ${sizes.xSmall}px)`,
    marginTop: isMultiLine ? -2 : undefined,
    lineHeight: 0,
}));

interface StyledContentAfterProps {
    layout: MenuLayoutOptions;
}
const StyledContentAfter = styled("div")<StyledContentAfterProps>(
    ({ layout }) => `
    margin-left: var(--sds--menu-item--gap--${layout}, ${sizes.xSmall}px);
    align-self: center;
    line-height: 0;
`
);

interface ShiftyDivProps {
    indent: boolean;
    themeMode: RequiredThemeKeys;
}

const ShiftyDiv = styled("div")<ShiftyDivProps>`
    flex-grow: 1;
    line-height: 18px;
    align-self: center;
    padding-left: ${({ indent, themeMode }) => (indent ? menuTheme.hasContentLeftPadding(themeMode) : "0")};
    overflow: hidden;
    text-overflow: ellipsis;
    .sds-type-ramp-v2 {
        max-width: 100%;
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-top: 5px;
    }
`;

const ContentBelowWrapper = styled("div")`
    margin-top: 2px;
`;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface MenuItemProps extends MenuItemBaseProps {
    /**
     * Content to display after the menuitem's text.
     */
    contentAfter?: ReactNode;

    /**
     * Content to display before the menuitem's text.
     * If there is nothing passed here and this item's parent Menu sets
     * hasContentBefore to true, this item will shift its text over to
     * account for the gap.
     */
    contentBefore?: ReactNode;

    /**
     * Content to display below the menuitem's text.
     */
    contentBelow?: ReactNode;
}

/**
 * Simple wrapper around a baseMenuItem to allow simple menu items w/ no icons, checkmarks, etc.
 */
export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
    ({ children, contentBefore, contentAfter, contentBelow, isDisabled, ...rest }, ref) => {
        const { hasContentBefore, layout } = useMenuParentContext();
        const { key } = useTheme();
        const themeMode = key as ThemeKey;
        const isMultiline = !!contentBelow;
        return (
            <MenuItemDefaultStyle {...rest} isDisabled={isDisabled || false} ref={ref} isMultiLine={isMultiline}>
                {(contentBefore && (
                    <StyledContentBefore layout={layout} isMultiLine={isMultiline}>
                        {contentBefore}
                    </StyledContentBefore>
                )) ||
                    null}
                <ShiftyDiv indent={hasContentBefore && !contentBefore} themeMode={themeMode}>
                    {children}
                    {(contentBelow && <ContentBelowWrapper>{contentBelow}</ContentBelowWrapper>) || null}
                </ShiftyDiv>
                {(contentAfter && <StyledContentAfter layout={layout}>{contentAfter}</StyledContentAfter>) || null}
            </MenuItemDefaultStyle>
        );
    }
);

MenuItem.displayName = "MenuItem";
